import React, { useCallback, useEffect, useState, useMemo, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { ListAlt } from '@mui/icons-material';
import CascoInsuranceGeneralData from './CascoInsuranceGeneralData';
import CascoQuotationListCard from './CascoQuotationListCard';
import { FakeText } from '@totalsoft/rocket-ui';
import CollapseOrBlankCard from 'components/common/CollapseOrBlankCard';
import { useApolloClient, useReactiveVar, useSubscription } from '@apollo/client';
import {
  INSURANCE_SIMULATION_SAVED,
  CONVENTIONS_QUOTATIONS_SAVED,
  INSURANCE_OWNER_APPLICATION_CODE_UPDATED,
  INSURANCE_FINANCING_OFFER_SAVED,
  INSURANCE_STATE_CODE_UPDATED_FAILED,
  INSURANCE_QUOTATIONS_STATUS_CHANGED,
  POLICY_DOCUMENT_NUMBER_SAVED
} from '../../../subscriptions';
import {
  INSURANCE_SIMULATION_SAVED_EVENT,
  CONVENTIONS_QUOTATIONS_SAVED_EVENT,
  INSURANCE_FINANCING_OFFER_SAVED_EVENT
} from '../../../eventTypes';
import { useChangeTrackingLens, get, set } from '@totalsoft/change-tracking-react';
import { Grid, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { emptyArray } from 'constants/emptyConst';
import { useTranslation } from 'react-i18next';
import { INSURANCE_SIMULATION_FOR_CASCO, LEASING_OFFER_INSURANCE_SIMULATION_IDS, QUOTATIONS_FOR_CASCO } from '../../../queries';
import { useClientQueryWithErrorHandling, useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { useToast, useActivityIndicatorContext } from 'utils/hooks';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { ownInsurance } from 'constants/ownInsurance';
import NegotiationContainer from '../../negotiation/NegotiationContainer';
import * as R from 'ramda';
import { env } from 'utils/env';
import { initialNegotiationState, quotationsStatus } from 'constants/insurance';
import OfferType from 'constants/offerType';
import { RightsContext } from 'providers/RightsContext';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import { invalidateApolloClientCache } from 'utils/apollo/common';
import CustomBackdrop from 'components/common/backdrop/CustomBackdrop';
import {
  cascoOutdateSimulationVar,
  cascoPolicyDocumentNumberVar,
  cascoQuotationsProcessingVar,
  cascoQuotationsStatusVar,
  cascoSelectedQuotationVar,
  cascoSelectionInProgressVar,
  commonGeneralDataVar
} from '../../../variables';
import useInsuranceParams from '../../../useInsuranceParams';

const CascoInsuranceContainer = props => {
  const { documentDetail, simulation, offerTypeId } = props;

  const { t } = useTranslation();
  const { onBeginCall, onEndCall } = useActivityIndicatorContext();
  const client = useApolloClient();
  const clientQuery = useClientQueryWithErrorHandling();
  const addToast = useToast();

  const { readOnlyFull, offerInsuranceWrite } = useContext(RightsContext);
  const { enableWebInsuranceNegotiation } = useContext(ApplicationFeaturesContext);
  const selectionInProgress = useReactiveVar(cascoSelectionInProgressVar);
  const { ownCasco } = useReactiveVar(commonGeneralDataVar);
  const cascoSelectedQuotation = useReactiveVar(cascoSelectedQuotationVar);
  const cascoQuotationsStatus = useReactiveVar(cascoQuotationsStatusVar);
  const policyDocumentNumber = useReactiveVar(cascoPolicyDocumentNumberVar);
  const { offerId, offerSiteId } = useInsuranceParams();

  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const quotationIsDirty = useRef(false);

  const [negotiationLens, , resetNegotiationLens] = useChangeTrackingLens(initialNegotiationState);

  // #region Queries

  const {
    loading: loadingSimulation,
    data: simulationData,
    refetch: refetchSimulation
  } = useQueryWithErrorHandling(INSURANCE_SIMULATION_FOR_CASCO, {
    variables: { id: simulation.id, offerId, offerSiteId },
    cacheEviction: true,
    skip: !expanded,
    onCompleted: data => {
      const _simulation = data?.insuranceSimulation;
      cascoSelectedQuotationVar({
        id: _simulation?.selectedQuotation?.cascoQuotation?.id,
        name: _simulation?.selectedQuotation?.cascoQuotation?.conventionInfo?.conventionName
      });
      cascoQuotationsStatusVar(_simulation?.quotationsStatus);
      cascoOutdateSimulationVar(false);
      cascoPolicyDocumentNumberVar(_simulation?.policyDocumentNumber);
    }
  });

  const {
    loading: loadingQuotations,
    data: quotationsData,
    refetch: refetchQuotations
  } = useQueryWithErrorHandling(QUOTATIONS_FOR_CASCO, {
    variables: { insuranceSimulationId: simulation.id },
    cacheEviction: true,
    skip: !expanded
  });

  // #endregion

  // #region Memos

  const currentPolicyMaintained = useMemo(() => simulation?.currentPolicyMaintained || false, [simulation]);
  const insuranceSimulation = useMemo(() => simulationData?.insuranceSimulation, [simulationData]);
  const quotations = useMemo(() => quotationsData?.quotations, [quotationsData]);
  const isOwnInsurance = useMemo(() => [ownInsurance.FirstYear, ownInsurance.EntirePeriod].includes(ownCasco), [ownCasco]);
  const quotationsProcessing = useMemo(() => cascoQuotationsStatus === quotationsStatus.PROCESSING, [cascoQuotationsStatus]);

  // #endregion

  // #region Side Effects

  useEffect(() => {
    if (quotationsProcessing) {
      onBeginCall();
    }
  }, [onBeginCall, quotationsProcessing]);

  useEffect(() => {
    const negotiationChecked = negotiationLens.negotiationChecked |> get;
    const selectedQuotations = negotiationLens.selectedQuotations |> get;

    if (!negotiationChecked) {
      if (selectedQuotations === emptyArray) return;
      (negotiationLens.selectedQuotations |> set)(emptyArray);
    } else {
      if (!cascoSelectedQuotation.id) return;
      if (R.any(x => x.id === cascoSelectedQuotation.id)(selectedQuotations)) return;
      (negotiationLens.selectedQuotations |> set)([{ id: cascoSelectedQuotation.id, name: cascoSelectedQuotation.name }]);
    }
  }, [negotiationLens, cascoSelectedQuotation]);

  // #endregion

  // #region Subscriptions

  useSubscription(INSURANCE_SIMULATION_SAVED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: async ({
      subscriptionData: {
        data: { insuranceSimulationSaved }
      }
    }) => {
      if (!insuranceSimulationSaved) return;

      if (insuranceSimulationSaved.finishSaving) {
        refetchSimulation();
        setSaving(false);
        onEndCall();
      }

      if (!insuranceSimulationSaved.keepQuotations) {
        refetchQuotations();
      }

      if (!insuranceSimulationSaved.silent) addToast(t('Notifications.' + INSURANCE_SIMULATION_SAVED_EVENT), 'info');

      const { data } = await clientQuery(LEASING_OFFER_INSURANCE_SIMULATION_IDS, {
        variables: { offerId, offerSiteId }
      });

      const documentDetails = data?.leasingOffer?.leasingSimulation?.documentDetails || emptyArray;
      documentDetails.forEach(documentDetail => {
        const insuranceSimulations = documentDetail?.insuranceSimulations || emptyArray;
        insuranceSimulations.forEach(insuranceSimulation => {
          if (insuranceSimulation.id === simulation.id) return;

          const insuranceSimulationCacheId = client.cache.identify({
            __typename: 'InsuranceSimulation',
            id: insuranceSimulation.id
          });

          client.cache.evict({ id: insuranceSimulationCacheId, fieldName: 'usageTypeCode' });
        });
      });

      client.cache.gc();
    }
  });

  useGlobalSubscription(INSURANCE_OWNER_APPLICATION_CODE_UPDATED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { insuranceOwnerApplicationCodeUpdated }
      }
    }) => {
      if (insuranceOwnerApplicationCodeUpdated?.ownerApplicationCode !== env.REACT_APP_LSNG_INSURANCE_APP_CODE)
        resetNegotiationLens(prevState => ({
          ...prevState,
          negotiationInProgress: false
        }));
    }
  });

  useSubscription(INSURANCE_FINANCING_OFFER_SAVED, {
    variables: { insuranceSimulationId: simulation.id, insuranceTypeCode: simulation.insuranceTypeCode },
    onSubscriptionData: ({
      subscriptionData: {
        data: { insuranceFinancingOfferSaved }
      }
    }) => {
      cascoOutdateSimulationVar(true);

      if (!insuranceFinancingOfferSaved) return;

      if (!insuranceFinancingOfferSaved?.quotationsInvalidated) {
        setSaving(false);
        onEndCall();
        addToast(
          t('Notifications.' + INSURANCE_FINANCING_OFFER_SAVED_EVENT, {
            data: { insuranceTypeName: simulation.insuranceTypeName }
          }),
          'info'
        );
      }
      invalidateApolloClientCache(client, { __typename: 'InsuranceSimulation', id: simulation.id }, [
        'selectedInsuranceQuotationId',
        'selectedQuotation',
        'externalInsuranceDocumentId',
        'externalInsuranceDocumentNumber',
        'currentPolicyMaintained'
      ]);
      refetchQuotations();
    }
  });

  useGlobalSubscription(INSURANCE_QUOTATIONS_STATUS_CHANGED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      cascoQuotationsStatusVar(data?.insuranceQuotationsStatusChanged?.quotationsStatus);
    }
  });

  useSubscription(CONVENTIONS_QUOTATIONS_SAVED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { conventionsQuotationsSaved }
      }
    }) => {
      if (conventionsQuotationsSaved) {
        setSaving(false);
        onEndCall();
        cascoQuotationsProcessingVar(false);
        if (!conventionsQuotationsSaved.silent) addToast(t('Notifications.' + CONVENTIONS_QUOTATIONS_SAVED_EVENT), 'info');
        refetchQuotations();
      }
    }
  });

  useGlobalSubscription(INSURANCE_STATE_CODE_UPDATED_FAILED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { externalInsuranceStateCodeUpdateFailed }
      }
    }) => {
      setSaving(false);
      onEndCall();
      if (externalInsuranceStateCodeUpdateFailed.message) addToast(externalInsuranceStateCodeUpdateFailed.message, 'error');
    }
  });

  useGlobalSubscription(POLICY_DOCUMENT_NUMBER_SAVED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { policyDocumentNumberSaved }
      }
    }) => {
      if (policyDocumentNumberSaved) {
        cascoPolicyDocumentNumberVar(policyDocumentNumberSaved?.policyDocumentNumber);
        addToast(t('Offer.Insurance.Notifications.CascoPolicyDocumentNumberSaved'), 'info');
      }
    }
  });

  // #endregion

  const isReadOnly = useCallback(
    isReadOnlyInternally => {
      if (
        readOnlyFull ||
        !offerInsuranceWrite ||
        simulationData?.insuranceSimulation?.ownerApplicationCode === env.REACT_APP_LSNG_INSURANCE_APP_CODE ||
        simulationData?.insuranceSimulation?.currentPolicyMaintained ||
        saving ||
        (negotiationLens.negotiationInProgress |> get) ||
        quotationsProcessing ||
        selectionInProgress ||
        policyDocumentNumber
      ) {
        return true;
      }

      if (isReadOnlyInternally) {
        return isReadOnlyInternally(negotiationLens);
      }

      const negotiationChecked = negotiationLens.negotiationChecked |> get;
      return negotiationChecked;
    },
    [
      readOnlyFull,
      offerInsuranceWrite,
      simulationData?.insuranceSimulation?.ownerApplicationCode,
      simulationData?.insuranceSimulation?.currentPolicyMaintained,
      saving,
      negotiationLens,
      quotationsProcessing,
      selectionInProgress,
      policyDocumentNumber
    ]
  );

  const onExpand = useCallback(
    (_, expanded) => {
      if (!expanded && quotationIsDirty.current) {
        if (!window.confirm(t('SaveChanges', { card: simulation.insuranceTypeName }))) {
          return;
        }
      }
      setExpanded(!expanded);
    },
    [t, simulation]
  );

  return (
    <CollapseOrBlankCard
      id={'cascoInsuranceCard'}
      icon={ListAlt}
      expanded={expanded}
      onToggle={onExpand}
      canExpand={ownCasco !== ownInsurance.NoInsurance}
      title={simulation.insuranceTypeName}
      subheader={
        <Grid container justifyContent='flex-start' alignItems='baseline' spacing={2} pt={2}>
          {(offerTypeId === OfferType.restructuring.id || offerTypeId === OfferType.cession.id) && (
            <Tooltip title={t('Offer.Insurance.CurrentPolicyMaintained')}>
              <FormControlLabel
                control={<Checkbox id={'casco_currentPolicyMaintained'} checked={currentPolicyMaintained} disabled />}
                label={t('Offer.Insurance.CurrentPolicyMaintained')}
              />
            </Tooltip>
          )}
          {ownCasco === ownInsurance.FirstYear || ownCasco === ownInsurance.EntirePeriod ? (
            <Grid item>{t('Offer.Insurance.OwnInsuranceSelected')}</Grid>
          ) : (
            ownCasco === ownInsurance.NoInsurance && <Grid item>{t('Offer.Insurance.NoInsurance')}</Grid>
          )}
        </Grid>
      }
      hideSubheaderOnExpand
    >
      {expanded ? (
        loadingSimulation || loadingQuotations ? (
          <FakeText lines={5} />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CascoInsuranceGeneralData
                data={simulationData}
                isReadOnly={isReadOnly}
                documentDetail={documentDetail}
                simulation={simulation}
                saving={saving}
                setSaving={setSaving}
                isOwnInsurance={isOwnInsurance}
              />
            </Grid>
            {!isOwnInsurance && quotations?.length > 0 && (
              <Grid item xs={12}>
                {enableWebInsuranceNegotiation && (
                  <NegotiationContainer
                    simulation={simulation}
                    lens={negotiationLens}
                    resetLens={resetNegotiationLens}
                    isReadOnly={isReadOnly}
                  />
                )}
              </Grid>
            )}
            <Grid item container xs={12}>
              <CascoQuotationListCard
                quotations={quotations}
                negotiationLens={negotiationLens}
                ownerApplicationCode={insuranceSimulation?.ownerApplicationCode}
                isReadOnly={isReadOnly}
                quotationIsDirty={quotationIsDirty}
              />
            </Grid>
            <CustomBackdrop message={t('Offer.Insurance.SelectionInProgress')} open={selectionInProgress}></CustomBackdrop>
          </Grid>
        )
      ) : (
        <></>
      )}
    </CollapseOrBlankCard>
  );
};

CascoInsuranceContainer.propTypes = {
  documentDetail: PropTypes.object.isRequired,
  simulation: PropTypes.object.isRequired,
  offerTypeId: PropTypes.number.isRequired
};

export default CascoInsuranceContainer;
